import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAnalysisStore } from "./analysis.store";
import {
  PRESETS,
  supportsLocalAnalysis,
  supportsThreadedAnalysis,
  estimateAnalysisSecondsPerGame,
} from "./analysis-presets";
import { AnalysisProgressPanel } from "./AnalysisProgressPanel";
import { useAnalysis } from "./useAnalysis";
import { useGames } from "../games/useGames";
import { useGamesStore } from "../games/games.store";
import { importRunner } from "../games/import-runner";
import { readDeviceCalibration, type DeviceCalibration } from "./analysis-calibration";
import { normalizeExactTimeControl } from "@chessla/shared";
import { ApiRequestError } from "@/shared/api/client";
import { useNotification } from "@/app/context/NotificationContext";
import { AnalysisHistoryList } from "./AnalysisHistoryList";
import { SingleGameAnalysisModal } from "./SingleGameAnalysisModal";
import { FullEngineDownloadDialog } from "./FullEngineDownloadDialog";
import { QuickAnalysisWarningDialog } from "./QuickAnalysisWarningDialog";
import { AnalysisPresetSelector, AnalysisQueueControls } from "./components";
import { layoutTokens } from "@/shared/theme";
import { EditorialPageHeader, FormatIcon } from "@/shared/components";
import { AnalysisTargetCard } from "./components/AnalysisTargetCard";
import { analysisClockPresets, analysisImportFilters, matchesAnalysisTarget } from "./analysis-target-selection";
import { clockSignatureGlyph, clockSignatureLabel } from "./clock-presentation";
import { useNavigate } from "react-router-dom";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction } from "@/features/entitlements/entitlement-policy";
import { useAnalysisPagePreferences } from "./useAnalysisPagePreferences";

/**
 * Analysis page — a thin view over useAnalysis + useAnalysisStore state. The
 * actual engine + game-by-game loop lives in analysisRunner (a module-level
 * singleton, see analysis-runner.ts), not here — this component only starts/stops
 * it and renders whatever state it reports. That split is what lets a batch keep
 * running if you navigate to Games/Chat/Profile mid-analysis and come back: this
 * component can fully unmount and remount without interrupting anything, since it
 * never owned the loop to begin with.
 */
export const AnalysisPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [analysisGameId, setAnalysisGameId] = useState<string | null>(null);
  const [fullEngineDialogOpen, setFullEngineDialogOpen] = useState(false);
  const [quickWarningOpen, setQuickWarningOpen] = useState(false);
  const [calibrations, setCalibrations] = useState<Record<string, DeviceCalibration>>({});
  const { selection, setSelection, preset } = useAnalysisPagePreferences();
  const { format: selectedFormat, clock: selectedClock } = selection;
  const isRunning = useAnalysisStore((state) => state.isRunning);
  const hasJobs = useAnalysisStore((state) => state.jobs.length > 0);
  const fullEngineDownloaded = useAnalysisStore((state) => state.fullEngineDownloaded);
  const importProgress = useGamesStore((state) => state.importProgress);
  const { unanalyzed, loading, startAnalysis, stopAnalysis, refreshUnanalyzed } = useAnalysis();
  const { games, summary, refreshGames } = useGames();
  const localAnalysisAvailable = supportsLocalAnalysis();
  const deepAnalysisAvailable = localAnalysisAvailable && supportsThreadedAnalysis();
  const selectedAnalysisAvailable = localAnalysisAvailable && (!preset.useFullEngine || deepAnalysisAvailable);
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const filteredUnanalyzed = useMemo(
    () =>
      unanalyzed.filter((game) =>
        matchesAnalysisTarget(
          game,
          selectedFormat,
          selectedClock,
          (exact) => normalizeExactTimeControl(exact) ?? "unknown",
        ),
      ),
    [selectedClock, selectedFormat, unanalyzed],
  );
  const queueGames = filteredUnanalyzed;
  const queueGate = resolveAnalysisAction(
    entitlementSnapshot,
    queueGames[0]?.id ?? "__queue__",
    false,
    queueGames[0]?.exactTimeControl,
  );
  const openReanalysis = (gameId: string) => {
    const gate = resolveAnalysisAction(entitlementSnapshot, gameId, true);
    if (gate.allowed) setAnalysisGameId(gameId);
    else void navigate(gate.cta === "manage_trial" ? "/subscription#trial" : "/subscription");
  };
  useEffect(() => {
    void Promise.all(PRESETS.map(async (item) => [item.id, await readDeviceCalibration(item)] as const)).then(
      (entries) => setCalibrations(Object.fromEntries(entries)),
    );
  }, []);

  const totalGames = summary?.totalGames ?? games.length;
  const queueEstimateMs =
    filteredUnanalyzed.length *
    estimateAnalysisSecondsPerGame(preset, undefined, calibrations[preset.id]?.typicalSeconds) *
    1_000;

  const formatItems = useMemo(
    () => [
      {
        id: "all",
        label: "All games",
        count: summary?.totalGames ?? games.length,
        countText: `${summary?.totalAnalyzedGames ?? games.filter((game) => game.analyzed).length} / ${summary?.totalGames ?? games.length} analyzed`,
      },
      ...Object.keys(analysisClockPresets)
        .map(
          (format) =>
            summary?.formats.find((item) => item.timeControl === format) ?? {
              timeControl: format,
              count: games.filter((game) => game.timeControl === format).length,
              analyzedCount: games.filter((game) => game.timeControl === format && game.analyzed).length,
            },
        )
        .sort((a, b) => b.count - a.count || a.timeControl.localeCompare(b.timeControl))
        .map((format) => ({
          id: format.timeControl,
          label: format.timeControl,
          count: format.count,
          countText: `${format.analyzedCount} / ${format.count} analyzed`,
        })),
    ],
    [games, summary],
  );
  const clockItems = useMemo(() => {
    const matching = unanalyzed.filter((game) => selectedFormat === "all" || game.timeControl === selectedFormat);
    const counts = new Map<string, { total: number; analyzed: number }>();
    for (const clock of analysisClockPresets[selectedFormat] ?? []) counts.set(clock, { total: 0, analyzed: 0 });
    (
      summary?.clocks ??
      matching.map((game) => ({
        timeControl: game.timeControl,
        exactTimeControl: game.exactTimeControl,
        count: 1,
        analyzedCount: 0,
      }))
    )
      .filter((clock) => selectedFormat === "all" || clock.timeControl === selectedFormat)
      .forEach((clock) => {
        const key = normalizeExactTimeControl(clock.exactTimeControl) ?? "unknown";
        const current = counts.get(key) ?? { total: 0, analyzed: 0 };
        counts.set(key, { total: current.total + clock.count, analyzed: current.analyzed + clock.analyzedCount });
      });
    return [
      ...[...counts.entries()]
        .sort(([, left], [, right]) => right.total - left.total)
        .map(([clock, value]) => ({
          key: clock,
          label: clockSignatureLabel(clock),
          glyph: clockSignatureGlyph(clock),
          count: value.total,
          countText: `${value.analyzed} / ${value.total} analyzed`,
        })),
      {
        key: "all",
        label: "All clocks",
        glyph: "∞",
        count: [...counts.values()].reduce((total, value) => total + value.total, 0),
        countText: `${[...counts.values()].reduce((total, value) => total + value.analyzed, 0)} / ${[...counts.values()].reduce((total, value) => total + value.total, 0)} analyzed`,
      },
    ];
  }, [selectedFormat, summary, unanalyzed]);
  useEffect(() => {
    if (summary && !formatItems.some((item) => item.id === selectedFormat))
      setSelection((current) => ({ ...current, format: "all", clock: "all" }));
  }, [formatItems, selectedFormat, summary, setSelection]);
  useEffect(() => {
    if (summary && !clockItems.some((item) => item.key === selectedClock))
      setSelection((current) => ({ ...current, clock: "all" }));
  }, [clockItems, selectedClock, summary, setSelection]);
  const handleImport = async () => {
    if (importRunner.running || isRunning) return;
    const ownerUserId = useGamesStore.getState().ownerUserId;
    try {
      const result = await importRunner.start("all", analysisImportFilters(selectedFormat, selectedClock));
      if (useGamesStore.getState().ownerUserId === ownerUserId)
        showNotification(`Nikola brought in ${result.imported} new game${result.imported === 1 ? "" : "s"}.`);
    } catch (error) {
      if (
        useGamesStore.getState().ownerUserId === ownerUserId &&
        !(error instanceof DOMException && error.name === "AbortError")
      )
        showNotification(error instanceof ApiRequestError ? error.message : "Unable to import games", "error");
    } finally {
      if (useGamesStore.getState().ownerUserId === ownerUserId) void Promise.all([refreshGames(), refreshUnanalyzed()]);
    }
  };
  const handleStartAnalysis = () => {
    if (importProgress != null || !selectedAnalysisAvailable) return;
    if (filteredUnanalyzed.length > 0 && !preset.verifiesTacticalPatterns) {
      setQuickWarningOpen(true);
      return;
    }
    if (preset.useFullEngine && !fullEngineDownloaded) {
      setFullEngineDialogOpen(true);
      return;
    }
    startAnalysis(preset, filteredUnanalyzed);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: layoutTokens.workspaceMaxWidth,
        mx: "auto",
        p: layoutTokens.workspacePagePadding,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: layoutTokens.sectionGap,
        overflowX: "clip",
      }}
    >
      <EditorialPageHeader
        kicker="ENGINE WORKSHOP"
        title="Analyze your games."
        metadata={
          <>
            <Typography component="span" sx={{ maxWidth: 520, fontSize: "inherit" }}>
              Produce objective evaluations and bounded lines locally in this browser. Coaching stays in Review and
              Chat.
            </Typography>
            <Typography component="span" sx={{ color: "primary.main", fontSize: "inherit", fontWeight: 900 }}>
              LOCAL ENGINE · SAVED FACTS
            </Typography>
          </>
        }
      />

      <AnalysisPresetSelector
        presets={PRESETS}
        preset={preset}
        calibrations={calibrations}
        onSelect={(item) => setSelection((current) => ({ ...current, preset: item.id }))}
        disabled={(item) => isRunning || !localAnalysisAvailable || (item.useFullEngine && !deepAnalysisAvailable)}
      />
      <Typography
        role={!localAnalysisAvailable ? "alert" : undefined}
        sx={{ color: !localAnalysisAvailable ? "error.main" : "text.secondary", fontSize: ".68rem", lineHeight: 1.5 }}
      >
        {!localAnalysisAvailable
          ? "Local analysis needs a browser with Web Workers and WebAssembly. Update Safari, Chrome, Edge, or Firefox and try again."
          : deepAnalysisAvailable
            ? "Completed games save one by one. You can use other Chessla pages, but keep this browser open until the batch ends."
            : "Quick and Regular use the universal single-thread engine here. Deep needs shared-memory support. Completed games save one by one."}
      </Typography>

      <Box>
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "baseline" },
            justifyContent: "space-between",
            gap: { xs: 0.65, sm: 2 },
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.12em" }}>
              02 / TARGET GAME FILTERS
            </Typography>
            <Typography sx={{ mt: 0.4, fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.03em" }}>
              Choose your games.
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", maxWidth: 220, textAlign: { xs: "left", sm: "right" } }}
          >
            {isRunning || importProgress
              ? "Filters lock while games are processing."
              : "Filter which games to import and analyze."}
          </Typography>
        </Box>
        <Box
          role="group"
          aria-label="Target game format"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
            gap: layoutTokens.controlGap,
          }}
        >
          {[...formatItems.filter((item) => item.id !== "all"), ...formatItems.filter((item) => item.id === "all")].map(
            (item) => (
              <AnalysisTargetCard
                key={item.id}
                active={selectedFormat === item.id}
                disabled={isRunning || importProgress != null}
                glyph={<FormatIcon format={item.id} />}
                label={item.label}
                countText={item.countText}
                onClick={() => {
                  setSelection((current) => ({ ...current, format: item.id, clock: "all" }));
                }}
              />
            ),
          )}
        </Box>
        {selectedFormat !== "all" && (
          <Box
            role="group"
            aria-label="Target game clock"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
              gap: layoutTokens.controlGap,
              mt: 2,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {clockItems.map((clock) => (
              <AnalysisTargetCard
                key={clock.key}
                active={selectedClock === clock.key}
                disabled={isRunning || importProgress != null}
                glyph={clock.glyph}
                label={clock.label}
                countText={clock.countText}
                onClick={() => setSelection((current) => ({ ...current, clock: clock.key }))}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
        <AnalysisQueueControls
          importProgress={importProgress}
          isRunning={isRunning}
          isLoading={loading}
          analysisAvailable={selectedAnalysisAvailable}
          entitlementAllowed={queueGate.allowed}
          entitlementLabel={
            queueGate.cta === "manage_trial" ? "MANAGE TRIAL" : queueGate.cta === "join_club" ? "JOIN CLUB" : undefined
          }
          unanalyzedCount={filteredUnanalyzed.length}
          totalGames={totalGames}
          queueEstimateMs={queueEstimateMs}
          onStopImport={() => importRunner.stop()}
          onImport={() => void handleImport()}
          onToggleAnalysis={isRunning ? stopAnalysis : handleStartAnalysis}
          onEntitlement={() =>
            void navigate(queueGate.cta === "manage_trial" ? "/subscription#trial" : "/subscription")
          }
        />
      </Box>

      {(hasJobs || isRunning) && <AnalysisProgressPanel />}
      <Box sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "baseline" },
            justifyContent: "space-between",
            gap: { xs: 0.65, sm: 2 },
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.12em" }}>
              04 / ENGINE ARCHIVE
            </Typography>
            <Typography sx={{ mt: 0.4, fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.03em" }}>
              Your analyzed games.
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", maxWidth: 220, textAlign: { xs: "left", sm: "right" } }}
          >
            Open a result to inspect the saved engine facts.
          </Typography>
        </Box>
        <AnalysisHistoryList onReanalyze={openReanalysis} />
      </Box>

      <SingleGameAnalysisModal
        gameId={analysisGameId}
        onClose={() => setAnalysisGameId(null)}
        onComplete={() => Promise.all([refreshGames(), refreshUnanalyzed()]).then(() => undefined)}
      />
      <FullEngineDownloadDialog
        open={fullEngineDialogOpen}
        onClose={() => setFullEngineDialogOpen(false)}
        onConfirm={() => {
          setFullEngineDialogOpen(false);
          startAnalysis(preset, filteredUnanalyzed);
        }}
      />
      <QuickAnalysisWarningDialog
        open={quickWarningOpen}
        onClose={() => setQuickWarningOpen(false)}
        onContinueQuick={() => {
          setQuickWarningOpen(false);
          startAnalysis(preset, filteredUnanalyzed);
        }}
        onUseRegular={() => {
          const regularPreset = PRESETS[1];
          setQuickWarningOpen(false);
          setSelection((current) => ({ ...current, preset: regularPreset.id }));
          startAnalysis(regularPreset, filteredUnanalyzed);
        }}
      />
    </Box>
  );
};
